import React, { useState, useMemo } from 'react';
import { useCentroOperaciones } from '../../hooks/useCentroOperaciones';
import DashboardDirectiva from './DashboardDirectiva';
import TablaCostosSL from './TablaCostosSL';
import FormularioProducto from './FormularioProducto';
import { ClipLoader } from 'react-spinners';
import toast from 'react-hot-toast';
import { db } from '../../firebase';
import { collection, addDoc } from 'firebase/firestore';
import { FaChartBar, FaProjectDiagram, FaExclamationTriangle, FaSearchDollar, FaCheckCircle, FaClock } from 'react-icons/fa';

// Importamos el JSON de la carpeta src
import datosLimpios from '../../productos_limpios.json';

// --- PANEL DE AUDITORÍA DE TIEMPOS ---
const PanelAuditoriaTiempos = ({ productos }) => {
    const formatCurrency = (value) => Number(value).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' });

    const analisis = useMemo(() => {
        if (!productos.length) return { topConsumo: [], criticos: 0, sospechosos: 0, promedio: 0 };
        
        const ordenados = [...productos].sort((a, b) => (parseFloat(b.horas) || 0) - (parseFloat(a.horas) || 0));
        let criticos = 0;
        let sospechosos = 0;
        let totalHoras = 0;

        ordenados.forEach(p => {
            const h = parseFloat(p.horas) || 0;
            totalHoras += h;
            if (h >= 40) criticos++; 
            if (h > 0 && h <= 1) sospechosos++; 
        });

        return {
            topConsumo: ordenados,
            criticos,
            sospechosos,
            promedio: (totalHoras / productos.length).toFixed(1)
        };
    }, [productos]);

    return (
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 animate-in fade-in duration-300">
            <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                <FaSearchDollar className="text-teal-600" /> Análisis de Coherencia de Tiempos
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-red-50 p-4 rounded-lg border border-red-200 flex items-center gap-3">
                    <FaExclamationTriangle className="text-red-500 text-3xl" />
                    <div>
                        <p className="text-xs font-bold text-red-800 uppercase">Riesgo Sobreprecio (&gt;40h)</p>
                        <p className="text-2xl font-black text-red-900">{analisis.criticos} Productos</p>
                    </div>
                </div>
                <div className="bg-orange-50 p-4 rounded-lg border border-orange-200 flex items-center gap-3">
                    <FaClock className="text-orange-500 text-3xl" />
                    <div>
                        <p className="text-xs font-bold text-orange-800 uppercase">Riesgo Pérdida (&lt;1h)</p>
                        <p className="text-2xl font-black text-orange-900">{analisis.sospechosos} Productos</p>
                    </div>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 flex items-center gap-3">
                    <FaChartBar className="text-blue-500 text-3xl" />
                    <div>
                        <p className="text-xs font-bold text-blue-800 uppercase">Promedio Catálogo</p>
                        <p className="text-2xl font-black text-blue-900">{analisis.promedio} h / prod</p>
                    </div>
                </div>
            </div>

            <div className="overflow-x-auto border border-gray-200 rounded-md">
                <table className="w-full text-sm text-left text-gray-600">
                    <thead className="text-xs text-gray-700 uppercase bg-gray-100 font-bold border-b border-gray-200">
                        <tr>
                            <th className="px-4 py-3">Diagnóstico</th>
                            <th className="px-4 py-3">Producto</th>
                            <th className="px-4 py-3 text-center">Horas Asignadas</th>
                            <th className="px-4 py-3 text-right">Coste Mat.</th>
                            <th className="px-4 py-3 text-right">Dpto</th>
                        </tr>
                    </thead>
                    <tbody>
                        {analisis.topConsumo.map(item => {
                            const horas = parseFloat(item.horas) || 0;
                            let estado = { color: 'text-green-600', bg: 'bg-green-50', icon: <FaCheckCircle />, texto: 'Equilibrado' };
                            
                            if (horas >= 40) {
                                estado = { color: 'text-red-600', bg: 'bg-red-50', icon: <FaExclamationTriangle />, texto: 'Revisar (Muy Alto)' };
                            } else if (horas > 0 && horas <= 1) {
                                estado = { color: 'text-orange-600', bg: 'bg-orange-50', icon: <FaClock />, texto: 'Revisar (Muy Bajo)' };
                            }

                            return (
                                <tr key={item.id} className="border-b border-gray-100 hover:bg-gray-50">
                                    <td className="px-4 py-2">
                                        <span className={`flex items-center gap-1 text-xs font-bold px-2 py-1 rounded ${estado.bg} ${estado.color} w-max`}>
                                            {estado.icon} {estado.texto}
                                        </span>
                                    </td>
                                    <td className="px-4 py-2 font-medium text-gray-900">{item.producto}</td>
                                    <td className={`px-4 py-2 text-center font-black ${estado.color}`}>{horas} h</td>
                                    <td className="px-4 py-2 text-right font-mono">{formatCurrency(item.materiales)}</td>
                                    <td className="px-4 py-2 text-right font-bold">{item.departamento || 'C'}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default function CentroOperaciones() {
    const { productos, cargando, parametrosSL, calcularEstructuraCosto, calcularPVP, eliminarProducto, agregarProducto, editarProducto } = useCentroOperaciones();
    const [inyectando, setInyectando] = useState(false);
    const [activeTab, setActiveTab] = useState('mando');

    // Variables sinceras de la capacidad operativa de la nave
    const [diasLaborables, setDiasLaborables] = useState(22);
    const [horasDiarias, setHorasDiarias] = useState(8);
    const [departamentos, setDepartamentos] = useState(2);
    const [metaParques, setMetaParques] = useState(3);
    
    const [gastos, setGastos] = useState([
        { id: 1, nombre: 'Alquiler', monto: 2000.00 },
        { id: 2, nombre: 'Luz', monto: 200.00 },
        { id: 3, nombre: 'Alarma', monto: 60.00 },
        { id: 4, nombre: 'Coches', monto: 900.00 },
        { id: 5, nombre: 'Digi', monto: 25.00 },
        { id: 6, nombre: 'Mantenimiento', monto: 200.00 },
        { id: 7, nombre: 'Transporte', monto: 0.00 },
        { id: 8, nombre: 'Página Web', monto: 79.00 },
        { id: 9, nombre: 'Quipu', monto: 43.00 },
        { id: 10, nombre: 'Gestor', monto: 181.00 },
        { id: 11, nombre: 'Administracion', monto: 7579.00 },
        { id: 12, nombre: 'Cuota Autónomo', monto: 500.00 },
        { id: 13, nombre: 'Gasolina', monto: 500.00 }
    ]);

    const totalGastosMensuales = useMemo(() => {
        return gastos.reduce((sum, g) => sum + (parseFloat(g.monto) || 0), 0);
    }, [gastos]);

    const prorrateoHoraAutonomo = useMemo(() => {
        // Fórmula matemática sincera: (Gastos / Dias / Horas / Deptos) / Parques Meta
        const divisorTotal = (diasLaborables || 22) * (horasDiarias || 8) * (departamentos || 2) * (metaParques || 3);
        return totalGastosMensuales / divisorTotal;
    }, [totalGastosMensuales, diasLaborables, horasDiarias, departamentos, metaParques]);

    const calcularEstructuraCostoDinamico = (materialesBase, horasProduccion) => {
        return calcularEstructuraCosto(materialesBase, horasProduccion, prorrateoHoraAutonomo);
    };

    const inyectarDatos = async () => {
        const confirm = window.confirm("¿Seguro que quieres inyectar los 157 productos limpios a Firebase?");
        if (!confirm) return;

        setInyectando(true);
        const toastId = toast.loading("Inyectando la matriz en Firebase...");
        const colRef = collection(db, "costos_produccion");

        try {
            let count = 0;
            for (const item of datosLimpios) {
                if (item.producto.includes("GASTOS FIJOS") || item.producto === "Check list") continue;

                await addDoc(colRef, {
                    producto: item.producto,
                    materiales: item.materiales,
                    horas: item.horas,
                    gastoFijoHora: prorrateoHoraAutonomo, 
                    departamento: item.departamento,
                    createdAt: new Date().toISOString()
                });
                count++;
            }
            toast.success(`¡ÉXITO TOTAL! ${count} productos inyectados correctamente.`, { id: toastId });
        } catch (error) {
            console.error(error);
            toast.error("Hubo un error en la inyección", { id: toastId });
        }
        setInyectando(false);
    };

    if (cargando) return <div className="flex justify-center items-center h-screen"><ClipLoader color="#1e3a8a" size={50} /></div>;

    const tabButtonStyle = "px-4 py-2 font-bold text-sm border-b-2 transition-colors flex items-center gap-2 outline-none";
    const activeStyle = "border-blue-900 text-blue-900";
    const inactiveStyle = "border-transparent text-gray-500 hover:text-gray-800 hover:border-gray-300";

    return (
        <div className="p-6 bg-gray-50 min-h-screen">
            {/* Cabecera Principal */}
            <div className="flex justify-between items-center bg-white p-6 rounded-lg shadow-sm mb-4 border-b-4 border-blue-900">
                <div>
                    <h2 className="text-xl font-bold text-gray-800">Centro de Operaciones Financieras</h2>
                    <p className="text-gray-500 text-xs">SCARJAV.SL | Gestión de Rentabilidad</p>
                </div>
                
                {productos.length === 0 && (
                    <button 
                        onClick={inyectarDatos} 
                        disabled={inyectando}
                        className={`px-4 py-2 text-white font-bold rounded shadow-lg transition-colors ${inyectando ? 'bg-gray-400' : 'bg-red-600 hover:bg-red-800'}`}
                    >
                        {inyectando ? 'Inyectando...' : '🚨 INYECTAR DATA MAESTRA'}
                    </button>
                )}
            </div>

            {/* Selector de Pestañas */}
            <div className="flex border-b border-gray-200 mb-6 px-2">
                <button 
                    onClick={() => setActiveTab('mando')} 
                    className={`${tabButtonStyle} ${activeTab === 'mando' ? activeStyle : inactiveStyle}`}
                >
                    <FaChartBar /> Cuadro de Mando
                </button>
                <button 
                    onClick={() => setActiveTab('analisis')} 
                    className={`${tabButtonStyle} ${activeTab === 'analisis' ? activeStyle : inactiveStyle}`}
                >
                    <FaProjectDiagram /> Análisis de Tiempos
                </button>
            </div>

            {/* Contenido Dinámico */}
            {activeTab === 'mando' ? (
                <div className="animate-in fade-in duration-300">
                    <DashboardDirectiva 
                        parametrosSL={parametrosSL}
                        gastos={gastos}
                        setGastos={setGastos}
                        metaParques={metaParques}
                        setMetaParques={setMetaParques}
                        diasLaborables={diasLaborables}
                        setDiasLaborables={setDiasLaborables}
                        horasDiarias={horasDiarias}
                        setHorasDiarias={setHorasDiarias}
                        departamentos={departamentos}
                        setDepartamentos={setDepartamentos}
                        totalGastosMensuales={totalGastosMensuales}
                        prorrateoHoraAutonomo={prorrateoHoraAutonomo}
                    />
                    <FormularioProducto agregarProducto={agregarProducto} />
                    <TablaCostosSL 
                        productos={productos} 
                        calcularEstructuraCosto={calcularEstructuraCostoDinamico} 
                        calcularPVP={calcularPVP} 
                        editarProducto={editarProducto} 
                        eliminarProducto={eliminarProducto} 
                    />
                </div>
            ) : (
                <PanelAuditoriaTiempos productos={productos} />
            )}
        </div>
    );
}